// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Link } from '@marriott/mi-ui-library';
import clsx from 'clsx';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { HubHeroBasicLink, HubHeroBasicModel, HubHeroBasicProps } from './HubHeroBasic.types';
import { StyledHubHeroBasic } from './HubHeroBasic.styles';
import { debounce, getDirection, useDCADynamicModelAlloy } from '../../modules/helpers';
import Glide from '@glidejs/glide';

export const HubHeroBasic: FC<HubHeroBasicProps> = (props: HubHeroBasicProps) => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const componentValues: HubHeroBasicModel = targetData ? targetData : props?.model;

  /**
   * cardsPerView is 0 on intial render to avoid the flickering on page load....
   */
  const [cardsPerView, setCardsPerView] = useState(0);

  const [activeTab, setActiveTab] = useState(0);

  const componentId = componentValues?.componentId;

  /**
   * Hide's the arrow's by default and when the cardsPerView is less than the hubHeroBasicLinks length
   * post the calcuation's set's isSlideArrowsVisible flag to true....
   */
  const isSlideArrowsVisible = cardsPerView !== 0 ? componentValues?.hubHeroBasicLinks?.length > cardsPerView : false;

  const containerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tabItemsRef = useRef<any>([]);

  //returns the rtl/ltr
  const [direction, setDirection] = useState(false);

  /**
   * return's the direction ('rtl' / 'ltr')...
   */
  const dir = getDirection();

  useEffect(() => {
    const carouselRootSelector = `#${componentId}`;
    let glide: Glide.Properties | Glide.Static;
    const leftArrow = document?.querySelector(`#${componentId}_left_arrow`);
    const rightArrow = document?.querySelector(`#${componentId}_right_arrow`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preventDefault = (e: any) => e.preventDefault();
    if (document?.querySelector(carouselRootSelector)) {
      try {
        glide = new Glide(carouselRootSelector, {
          type: 'slider',
          perView: cardsPerView,
          startAt: 0,
          rewind: false,
          bound: true,
          direction: dir ? 'rtl' : 'ltr',
          gap: 1,
        }).mount({ ArrowDisabler });

        leftArrow?.addEventListener('touchstart', preventDefault);
        rightArrow?.addEventListener('touchstart', preventDefault);
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }
    return () => {
      if (glide) {
        (glide as Glide.Properties)?.destroy();
      }
      leftArrow?.removeEventListener('touchstart', preventDefault);
      rightArrow?.removeEventListener('touchstart', preventDefault);
    };
  }, [componentId, cardsPerView]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        // Only in effect when rewinding is disabled
        if (Glide.settings.rewind) {
          return;
        }
        const left = document?.querySelector('.glide__arrow--left');

        left?.classList.remove('is-disabled');
        Glide.on(['mount.after', 'run'], () => {
          // Filter out arrows_control
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') {
              continue;
            }
            // Update arrow states
            const leftArrow = controlItem?.querySelector(dir ? '.glide__arrow--right' : '.glide__arrow--left');
            const rightArrow = controlItem?.querySelector(dir ? '.glide__arrow--left' : '.glide__arrow--right');

            if (leftArrow) {
              if (Glide.index === 0) {
                leftArrow.classList.add('is-disabled'); // Disable on first slide
              } else {
                leftArrow.classList.remove('is-disabled'); // Enable on other slides
                leftArrow.classList.remove('glide__arrow--disabled'); // Enable on other slides
              }
            }

            if (rightArrow) {
              if (Glide.index === Components.Sizes?.length - Glide.settings.perView) {
                rightArrow.classList.add('is-disabled'); // Disable on last slide
              } else {
                rightArrow.classList.remove('is-disabled'); // Enable on other slides
                rightArrow.classList.remove('glide__arrow--disabled'); // Enable on other slides
              }
            }
          }
        });
      },
    };
  };

  const handleTabClick = (tabidx: number) => {
    setActiveTab(tabidx);
  };

  /**
   * Sets the Tabs perView on screen resize
   */

  const handleResize = () => {
    const divWidth = containerRef?.current?.offsetWidth || 0;

    const tabElements = tabItemsRef?.current;
    const tabElementswidth: number[] = [];

    /**
     * Calculating the Width of the all tab Elements
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tabElements?.map((el: any) => tabElementswidth?.push(el?.firstElementChild?.getBoundingClientRect()?.width));

    /**
     * Finds the longest Tab width
     */
    let longestTabWidth = 0;
    tabElementswidth?.map(width => width > longestTabWidth && (longestTabWidth = width));

    /**
     * Finds the tabs to be in the View
     */
    const tabsInAView = Math.floor(divWidth / longestTabWidth);

    /**
     * sets the Tabs per view
     */
    setCardsPerView(tabsInAView);
  };

  useEffect(() => {
    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);
    handleResize();
    window.addEventListener('resize', debouncedResize);
    setDirection(dir);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  const getLinkElements = (hubHeroBasicLinks: HubHeroBasicLink[], enableTracking: boolean, isAnchorLinks: boolean) => {
    return hubHeroBasicLinks.map((link: HubHeroBasicLink, index: number) => {
      return (
        <div key={index} className="hub-hero-basic-link" ref={el => (tabItemsRef.current[index] = el)}>
          <Link
            text={link?.ctaProperties.linkText || ''}
            linkClassName={clsx('m-button-s hub-hero-basic-cta-link p-0', enableTracking ? 'custom_click_track' : '', {
              active: activeTab === index,
            })}
            linkHref={`${isAnchorLinks ? '#' : ''}${link?.ctaProperties.linkUrl || ''}`}
            target={link?.ctaProperties.openInANewTab ? '_blank' : '_self'}
            linkType={link?.ctaProperties?.linkType}
            custom_click_track_value={enableTracking ? link?.clickTrackValue || '' : ''}
            callback={() => handleTabClick(index)}
          />
        </div>
      );
    });
  };

  return (
    <StyledHubHeroBasic className={componentValues?.styleclass}>
      <div className={clsx('container hub-hero-basic px-0', isSlideArrowsVisible ? 'pt-3' : '')}>
        <div className="hub-hero-basic-links-wrapper px-3 px-md-0">
          <div id={`${componentId}`} className={clsx(`glide col-12 px-0`)}>
            <div className={clsx('glide__track')} data-glide-el="track" ref={containerRef}>
              <ul
                className={clsx(
                  `hub-hero-basic-links`,
                  'glide__slides',
                  !isSlideArrowsVisible ? 'tabs-only no-slider' : ''
                )}
              >
                {componentValues?.hubHeroBasicLinks &&
                  getLinkElements(
                    componentValues?.hubHeroBasicLinks,
                    componentValues?.enableTracking,
                    componentValues?.isAnchorLinks
                  )}
              </ul>
            </div>
            {isSlideArrowsVisible && (
              <div className="center-align">
                <div className="carouselControlType1 tab-controls">
                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className={clsx(
                        direction
                          ? 'right-arrow glide__arrow--right flex-row-reverse'
                          : 'left-arrow glide__arrow--left',
                        'alt-arrow'
                      )}
                      id={`${componentId}_left_arrow`}
                      aria-label="left-arrow"
                      data-glide-dir={direction ? '>' : '<'}
                      data-custom_click_track_value={`internal`}
                    >
                      <span
                        className={clsx(direction ? 'icon-arrow-right' : 'icon-arrow-left')}
                        id={`${componentId}_left_arrow`}
                        data-id="left"
                      ></span>
                    </button>
                  </div>
                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className={clsx(
                        direction
                          ? 'left-arrow glide__arrow--left flex-row-reverse'
                          : 'right-arrow glide__arrow--right',
                        'alt-arrow'
                      )}
                      id={`${componentId}_right_arrow`}
                      data-glide-dir={direction ? '<' : '>'}
                      aria-label="right-arrow"
                      data-custom_click_track_value={`internal`}
                    >
                      <span
                        className={clsx(direction ? 'icon-arrow-left' : 'icon-arrow-right')}
                        id={`${componentId}_right_arrow`}
                        data-id="right"
                      ></span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </StyledHubHeroBasic>
  );
};

export const HubHeroBasicConfig = {
  emptyLabel: 'LuxuryHubHeroBasic',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/hub-hero-basic/v1/hub-hero-basic`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HubHeroBasicEditable = (props: any) => (
  <div data-testid="luxury-HubHeroBasic" data-component-name="m-luxury-HubHeroBasic">
    <EditableComponent config={HubHeroBasicConfig} {...props}>
      <HubHeroBasic {...props} />
    </EditableComponent>
  </div>
);
